<template>
  <div v-if="listing" class="page-listing">
    <ListingView :listing="listing" />


    <LazyAdAdBar ad-display-location="listing_design_page_banner" />
  </div>
</template>

<script setup lang="ts">
import {Ref} from 'vue';
import {useFetch, useRoute} from '#app';
import {Listing} from '~/@types/listing';
import globalApiHeaders from '~/composables/globalApiHeaders';
import {ApiReturn} from '~/@types/common';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const route = useRoute();
const config = useRuntimeConfig();
const listing: Ref<Listing | null> = ref(null);
const {$analyticsTrack, $metaPixelTrackEvent} = useNuxtApp();

// DATA Fetching
//----------------------------------------------------------------------------------------------------------------------
const {data} = await useFetch(() => `/public/listings/show/${route.params.slug}`,
  { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
);
if (data.value) {
  if ((data.value as ApiReturn).code === 200 && (data.value as ApiReturn).data) {
    // Data is returned
    listing.value = (data.value as ApiReturn).data as Listing;
  } else if ((data.value as ApiReturn).code === 404) {
    // No data is returned. Page not found
    throw createError({statusCode: 404, message: 'Listing not found', fatal: true});
  } else {
    // Something went wrong
    throw createError({statusCode: 500, message: 'Something went wrong', fatal: true});
  }
}

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: listing?.value?.slug ? `https://www.newhomes.co.nz/listing/${listing.value.slug}/view` : 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const seoMetaData = getSeoMeta();
useSeoMeta({
  title: seoMetaData.title,
  description: seoMetaData.description,
  ogTitle: seoMetaData.title,
  ogDescription: seoMetaData.description,
  ogImage: seoMetaData.imageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: seoMetaData.title,
  twitterDescription: seoMetaData.description,
  twitterImage: seoMetaData.imageUrl,
});

function getSeoMeta (): {title: string, description: string,imageUrl: string} {
  const listingName = listing.value?.name ?? 'Listing';
  const seoImageUrl = listing.value?.heroImageUrl ?? 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg';
  const listingType = listing.value?.listingTypes?.data[0] ?? 'Listing Type';
  if (listing.value && listing.value.seo) {
    const { title, description, imageUrl } = listing.value.seo;
    return {
      title: title || `${listingName} | New Homes - Explore This ${listingType} listing in Detail`,
      description: description || `Explore ${listingName} on New Homes. Detailed info about bedrooms, bathrooms, floor area, price, and more. View images, video, and contact details.`,
      imageUrl: imageUrl || seoImageUrl,
    };
  } else {
    return {
      title: `${listingName} | New Homes - Explore This ${listingType} listing in Detail`,
      description: `Explore ${listingName} on New Homes. Detailed info about bedrooms, bathrooms, floor area, price, and more. View images, video, and contact details.`,
      imageUrl: seoImageUrl,
    };
  }
}

/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  triggerAnalyticsTracking();
});

/**
 * Trigger analytics tracking
 */
function triggerAnalyticsTracking () {
  if (!listing.value) return;

  // Track analytics event
  $analyticsTrack({
    action: 'view',
    entity: 'Listing',
    property: 'listing',
    entitySlug: listing.value.slug,
    displayedLocation: 'listing page',
    eventData: {
      userIntention: 'active',
      component: 'listing page',
    },
  });

  // Track meta event
  $metaPixelTrackEvent('ViewContent', {
    content_name: listing.value.name,
    content_category: listing.value?.listingTypes.data.join(', ') ?? 'Listing',
    content_ids: [listing.value.did],
    content_type: 'Listing',
  });
}
</script>

<style lang="scss" scoped>
.page-listing {
  .__feature-wrapper {
    margin-top: 30px;
  }
}
</style>

